import * as Styled from './EventFeedbackModal.styles';

import { Box, Card, CardContent, CardMedia, CircularProgress, Stack, Typography, useTheme } from '@mui/material';

import BaseModal from '../BaseModal/BaseModal';
import { FormProvider, useForm } from 'react-hook-form';
import Slider from '../../components/Slider/Slider';
import { Input } from '../../components';
import { Event } from '../../types/event';
import { formatEventTime } from '../../utils/event_utils';
import { getMonthAndDayFromMilliseconds } from '../../utils/utils';
import { useSelector } from 'react-redux';
import { GatherDefaultImage } from '../../assets';
import { useAppDispatch } from '../../store/hooks';
import { submitFeedback, SubmitFeedbackRequest, submitNoFeedback } from '../../store/actions/liveEventActions';
import { RootState } from '../../store/store';
import { useEffect } from 'react';

interface EventData {
  event: Event | null;
}

type EventFeedbackModalProps = {
  open: any;
  onClose: any;
  eventData?: EventData;
  onTransitionExited?: any;
};

const FEEDBACK_SUBMITTED = "live-event/feedback/submit/fulfilled";
const NO_FEEDBACK_SUBMITTED = "live-event/no/feedback/submit/fulfilled";


const marks = [
  {
    value: 0,
    label: '0',
  },
  {
    value: 1,
    label: '1',
  },
  {
    value: 2,
    label: '2',
  },
  {
    value: 3,
    label: '3',
  },
  {
    value: 4,
    label: '4',
  },
  {
    value: 5,
    label: '5',
  },
  {
    value: 6,
    label: '6',
  },
  {
    value: 7,
    label: '7',
  },
  {
    value: 8,
    label: '8',
  },
  {
    value: 9,
    label: '9',
  },
  {
    value: 10,
    label: '10',
  },
];



const EventFeedbackFormModal = ({ open, onClose, eventData, onTransitionExited }: EventFeedbackModalProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const eventTimings = eventData?.event ? formatEventTime(eventData?.event?.startDateTime, eventData?.event?.endDateTime) : undefined
  const eventDay = eventData?.event ? getMonthAndDayFromMilliseconds(eventData?.event?.startDateTime) : undefined;
  const imageUrl = `${eventData?.event?.eventHeroS3Url || GatherDefaultImage}`

  const user = useSelector((state: RootState) => state.user);
  const feedbackLoading = useSelector((state: RootState) => state.liveEvent.eventFeedback.loading);
  const feedbackSubmissionType = useSelector((state: RootState) => state.liveEvent.eventFeedback.type);

  const formMethods = useForm({
      defaultValues: {
        feedbackScoreSlider: 0,
        feedbackMessage: "",
      },
      mode: "onChange",
    });

  const handleSubmit = async () => {
    const formValues = formMethods.getValues();

    const payload = {
      participantEmail: user.email,
      eventFeedbackResponse: {
        rating: formValues.feedbackScoreSlider,
        additionalComments: formValues.feedbackMessage
      }
    }

    if(eventData?.event?.eventId){
      dispatch(submitFeedback({eventId: eventData.event?.eventId, payload }))
    }
  }

  const handleNoFeedback = async () => {

    const payload: SubmitFeedbackRequest = {
      participantEmail: user.email,
      eventFeedbackResponse: {
        rating: -2,
        additionalComments: ""
      }
    }

    if(eventData?.event?.eventId){
      dispatch(submitNoFeedback({eventId: eventData.event?.eventId, payload }))
    }
  }

  useEffect(() => {
    const handleClose = () => {
      formMethods.reset();
      onClose();
    }

    if(!feedbackLoading && feedbackSubmissionType === FEEDBACK_SUBMITTED){
      handleClose()
      onTransitionExited(true);
    } else if(!feedbackLoading && feedbackSubmissionType === NO_FEEDBACK_SUBMITTED) {
      handleClose();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedbackLoading, feedbackSubmissionType])


  return (
    <BaseModal open={open} onClose={onClose} fullScreenMobile desktopWidth={"auto"} overflow='scroll' showCloseButton={false}>
      <Styled.ModalContainer>
          <Card sx={{display: "flex", width: "100%", border: "solid 1px", }}>
            <CardMedia
              component="img"
              sx={{ width: 151 }}
              image={imageUrl}
              alt=""
              />
              <CardContent sx={{display: "flex", width: "100%", flexDirection: "column"}}>
                <Box>
                  <Styled.ModalBodyMB2 variant='modalBody'>
                      {`${eventDay} `}
                  </Styled.ModalBodyMB2>
                  <Styled.ModalBodyMB2 variant='modalBody'>
                    {`${eventTimings?.startTime.time}`}
                    <Styled.SuperScript>{`${eventTimings?.startTime.ampm}`}</Styled.SuperScript>
                    {" - "}
                      {`${eventTimings?.endTime.time}`}
                    <Styled.SuperScript>{`${eventTimings?.endTime.ampm}`}</Styled.SuperScript>
                  </Styled.ModalBodyMB2>
                </Box>
                <Styled.EventDetailsTitle variant="h3">
                  {eventData?.event?.title}
                </Styled.EventDetailsTitle>
                {/* <Styled.EventDescription variant="body2">
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(eventData?.event?.description || ""),
                    }}
                  />
                </Styled.EventDescription> */}
              </CardContent>
          </Card>
          <FormProvider {...formMethods}>
          <Box>
            <Typography variant='h1'>
              How was the event?
            </Typography>
            <Typography>
            Please help us enahance your event experience by providing feedback.
            </Typography>
          </Box>
          <Box>
            <Typography>
            Rate your overall satisfaction with the event by using the slider below.*
            </Typography>
            <Box sx={{paddingX: "30px", paddingTop: "10px"}}>
              <Slider
                aria-label="feedback-score"
                name='feedbackScoreSlider'
                control={formMethods.control}
                valueLabelDisplay="auto"
                step={1}
                marks={marks}
                min={0}
                max={10}
              />
            </Box>
          </Box>
          <Box sx={{display: "flex", flexDirection: "column", rowGap: "20px" }}>
            <Typography>
            Why did you rate this way? Please provide context or suggestions below. (optional)
            </Typography>
            <Input
              id="standard-multiline-flexible"
              multiline
              rows={4}
              variant="outlined"
              control={formMethods.control}
              placeholder=''
              label=""
              name='feedbackMessage'
            />
          </Box>
          {!feedbackLoading ? <Stack
            direction={{ mobile: "column", desktop: "row" }}
            spacing={theme.spacing(1)}
            width="100%"
            justifyContent="flex-end"
          >
            <Styled.ActionButton
              variant="outlined"
              onClick={handleNoFeedback}
            >
              Don't give feedback
            </Styled.ActionButton>
            <Styled.ActionButton
              variant="contained"
              type="submit"
              onClick={handleSubmit}
            >
              Submit
            </Styled.ActionButton>
          </Stack> : 
          <Box sx={{display: "flex", justifyContent: "center", width: "100%"}}>
            <CircularProgress/>
          </Box>}
          </FormProvider>
      </Styled.ModalContainer>
    </BaseModal>
  );
};

export default EventFeedbackFormModal;
